import { MonitoringProductImageService } from "@domain/product/monitoring/monitoring-product-image.service";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import Konva from "konva";
import { catchError, forkJoin, map, Observable, of, switchMap, throwError } from "rxjs";
import { fromFetch } from "rxjs/internal/observable/dom/fetch";

export type ProductImageTypes =
  | FloorplanGasWarningCenter
  | FloorplanAlarmDevice
  | FloorplanSignalElement
  | FloorplanPlasticSign
  | FloorplanTransmitter
  | FloorplanFlameDetector
  | FloorplanPatientMonitor
  | FloorplanCentralStation
  | FloorplanTelemetry
  | FloorplanKvm
  | FloorplanDisplay
  | FloorplanUtility;

export enum ImageType {
  TEXT_ICON = "TEXT_ICON",
  TRANSMITTER_ICON = "TRANSMITTER_ICON",
  TRANSMITTER_PLACEHOLDER_ICON = "TRANSMITTER_PLACEHOLDER_ICON",
  ALARM_DEVICE_ICON = "ALARM_DEVICE_ICON",
  ALARM_DEVICE_PLACEHOLDER_ICON = "ALARM_DEVICE_PLACEHOLDER_ICON",
  GAS_WARNING_CENTER_ICON = "GAS_WARNING_CENTER_ICON",
  GAS_WARNING_CENTER_PLACEHOLDER_ICON = "GAS_WARNING_CENTER_PLACEHOLDER_ICON",
  SIGNAL_ELEMENT_ICON = "SIGNAL_ELEMENT_ICON",
  SIGNAL_ELEMENT_PLACEHOLDER_ICON = "SIGNAL_ELEMENT_PLACEHOLDER_ICON",
  PLASTIC_SIGN_ICON = "PLASTIC_SIGN_ICON",
  PLASTIC_SIGN_PLACEHOLDER_ICON = "PLASTIC_SIGN_PLACEHOLDER_ICON",
  WARNING_SIGN_ICON = "WARNING_SIGN_ICON",
  REMOTE_SENSOR = "REMOTE_SENSOR",
  PATIENT_MONITOR_ICON = "PATIENT_MONITOR_ICON",
  PATIENT_MONITOR_PLACEHOLDER_ICON = "PATIENT_MONITOR_PLACEHOLDER_ICON",
  CENTRAL_STATION_ICON = "CENTRAL_STATION_ICON",
  CENTRAL_STATION_PLACEHOLDER_ICON = "CENTRAL_STATION_PLACEHOLDER_ICON",
  TELEMETRY_ICON = "TELEMETRY_ICON",
  TELEMETRY_PLACEHOLDER_ICON = "TELEMETRY_PLACEHOLDER_ICON",
  KVM_ICON = "KVM_ICON",
  KVM_PLACEHOLDER_ICON = "KVM_PLACEHOLDER_ICON",
  KVM_RECEIVER_ICON = "KVM_RECEIVER_ICON",
  DISPLAY_ICON = "DISPLAY_ICON",
  DISPLAY_PLACEHOLDER_ICON = "DISPLAY_PLACEHOLDER_ICON",
  UTILITY_ICON = "UTILITY_ICON",
  UTILITY_PLACEHOLDER_ICON = "UTILITY_PLACEHOLDER_ICON",
  FLAME_DETECTOR_ICON = "FLAME_DETECTOR_ICON",
  FLAME_DETECTOR_PLACEHOLDER_ICON = "FLAME_DETECTOR_PLACEHOLDER_ICON",
}

export enum IconColor {
  DEFAULT_COLOR = "#002766",
  GROUP_COLOR = "#0091fe",
  MULTI_SELECT_COLOR = "#1F65D6",
}

interface ImageTypeUrl {
  type: ImageType;
  url: string;
}

export class ImageFactoryKonva {
  private static readonly IMAGE_TYPE_URLS: ImageTypeUrl[] = [
    { type: ImageType.TEXT_ICON, url: "../../assets/floorplan-menu-icons/notiz_menu.svg" },
    { type: ImageType.TRANSMITTER_ICON, url: "../../assets/konva-icons/transmitter.svg" },
    { type: ImageType.TRANSMITTER_PLACEHOLDER_ICON, url: "../../assets/konva-icons/transmitter_platzhalter.svg" },
    { type: ImageType.ALARM_DEVICE_ICON, url: "../../assets/konva-icons/alarmmittel.svg" },
    { type: ImageType.ALARM_DEVICE_PLACEHOLDER_ICON, url: "../../assets/konva-icons/alarmmittel_platzhalter.svg" },
    { type: ImageType.GAS_WARNING_CENTER_ICON, url: "../../assets/konva-icons/gaswarnzentrale.svg" },
    { type: ImageType.GAS_WARNING_CENTER_PLACEHOLDER_ICON, url: "../../assets/konva-icons/gaswarnzentrale_platzhalter.svg" },
    { type: ImageType.SIGNAL_ELEMENT_ICON, url: "../../assets/konva-icons/leuchttransparent.svg" },
    { type: ImageType.SIGNAL_ELEMENT_PLACEHOLDER_ICON, url: "../../assets/konva-icons/leuchttransparent_platzhalter.svg" },
    { type: ImageType.PLASTIC_SIGN_ICON, url: "../../assets/konva-icons/kunststoffschild.svg" },
    { type: ImageType.PLASTIC_SIGN_PLACEHOLDER_ICON, url: "../../assets/konva-icons/kunststoffschild_platzhalter.svg" },
    { type: ImageType.WARNING_SIGN_ICON, url: "../../assets/warning-colored-odx.svg" },
    { type: ImageType.REMOTE_SENSOR, url: "../../assets/konva-icons/remote_sensor.svg" },
    { type: ImageType.PATIENT_MONITOR_ICON, url: "../../assets/konva-icons/patient_monitor.svg" },
    { type: ImageType.PATIENT_MONITOR_PLACEHOLDER_ICON, url: "../../assets/konva-icons/patient_monitor_placeholder.svg" },
    { type: ImageType.CENTRAL_STATION_ICON, url: "../../assets/konva-icons/central_station.svg" },
    { type: ImageType.CENTRAL_STATION_PLACEHOLDER_ICON, url: "../../assets/konva-icons/central_station_placeholder.svg" },
    { type: ImageType.TELEMETRY_ICON, url: "../../assets/konva-icons/telemetry.svg" },
    { type: ImageType.TELEMETRY_PLACEHOLDER_ICON, url: "../../assets/konva-icons/telemetry_placeholder.svg" },
    { type: ImageType.KVM_ICON, url: "../../assets/konva-icons/kvm.svg" },
    { type: ImageType.KVM_PLACEHOLDER_ICON, url: "../../assets/konva-icons/kvm_placeholder.svg" },
    { type: ImageType.KVM_RECEIVER_ICON, url: "../../assets/konva-icons/kvm_receiver.svg" },
    { type: ImageType.DISPLAY_ICON, url: "../../assets/konva-icons/display.svg" },
    { type: ImageType.DISPLAY_PLACEHOLDER_ICON, url: "../../assets/konva-icons/display_placeholder.svg" },
    { type: ImageType.UTILITY_ICON, url: "../../assets/konva-icons/utility.svg" },
    { type: ImageType.UTILITY_PLACEHOLDER_ICON, url: "../../assets/konva-icons/utility_placeholder.svg" },
    { type: ImageType.FLAME_DETECTOR_ICON, url: "../../assets/konva-icons/flame_detector.svg" },
    { type: ImageType.FLAME_DETECTOR_PLACEHOLDER_ICON, url: "../../assets/konva-icons/flame_detector_placeholder.svg" },
  ];

  private imagesByType: Map<ImageType, Konva.Image>;
  private productService: ProductService;
  private monitoringProductService: MonitoringProductService;
  private monitoringProductImageService: MonitoringProductImageService;
  private utilityProductService: UtilityProductService;

  constructor(
    imagesByType: Map<ImageType, Konva.Image>,
    productService: ProductService,
    monitoringProductService: MonitoringProductService,
    monitoringProductImageService: MonitoringProductImageService,
    utilityProductService: UtilityProductService,
  ) {
    this.imagesByType = imagesByType;
    this.productService = productService;
    this.monitoringProductService = monitoringProductService;
    this.monitoringProductImageService = monitoringProductImageService;
    this.utilityProductService = utilityProductService;
  }

  destroy() {
    Array.from(this.imagesByType.values()).forEach((image: Konva.Image) => image.destroy());
  }

  createImage(type: ImageType) {
    const image = this.imagesByType.get(type);
    if (!image) {
      throw Error(`No image found for type ${type}`);
    }
    return image.clone();
  }

  createProductImage$(floorplanItem: ProductImageTypes): Observable<Konva.Image> {
    const imageNode = new Konva.Image({
      image: undefined,
      width: 24,
      height: 24,
    });

    const applyIcon = () => {
      const fallbackIcon = this.getFallbackIconImage(floorplanItem);
      imageNode.image(fallbackIcon.image());
      this.removeBorder(imageNode);

      return of(imageNode);
    };

    const loadUrl$ = (url: string) => this.loadImage$(url, imageNode, IconColor.DEFAULT_COLOR).pipe(map(() => imageNode));
    const loadMain$ = (imageUrl: string) => loadUrl$(imageUrl).pipe(catchError(() => applyIcon()));

    return this.getImageUrl$(floorplanItem).pipe(
      switchMap((imageUrl) => {
        if (!imageUrl) {
          return applyIcon();
        }

        return loadMain$(imageUrl);
      }),
    );
  }

  setProductImage$(
    item: Konva.Group,
    imageNode: Konva.Image,
    floorplanItem: ProductImageTypes,
    isInGroup?: boolean,
  ): Observable<"main" | "fallback" | "icon"> {
    const color = isInGroup ? IconColor.GROUP_COLOR : IconColor.DEFAULT_COLOR;
    const applyIcon = () => {
      const fallback$ = this.setFinalFallbackIcon(item, floorplanItem, isInGroup);
      return (fallback$ ? fallback$ : of(undefined)).pipe(map(() => "icon" as const));
    };

    const loadMain$ = (imageUrl: string) =>
      this.loadImage$(imageUrl, imageNode, color).pipe(
        map(() => "main" as const),
        catchError(() => applyIcon()),
      );

    return this.getImageUrl$(floorplanItem).pipe(
      switchMap((imageUrl) => {
        if (!imageUrl) {
          return applyIcon();
        }

        return loadMain$(imageUrl);
      }),
    );
  }

  loadImage$(src: string, imageNode: Konva.Image, color: IconColor): Observable<HTMLImageElement> {
    return new Observable((observer) => {
      const img = new window.Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const maxSize = 24;
        const { scaledWidth, scaledHeight } = this.getScaledSize(img.width, img.height, maxSize);

        imageNode.image(img);
        imageNode.width(scaledWidth);
        imageNode.height(scaledHeight);

        this.setBorder(imageNode, color);

        observer.next(img);
        observer.complete();
      };
      img.onerror = () => {
        console.error("Image failed to load:", src);
        observer.error("Image load failed: " + src);
      };
      img.src = src;
    });
  }

  private setFinalFallbackIcon(item: Konva.Group, floorplanItem: ProductImageTypes, isInGroup?: boolean): Observable<void> {
    let request: Observable<"success"> | undefined;
    if (floorplanItem instanceof FloorplanGasWarningCenter) {
      request = this.setIcon$(ImageType.GAS_WARNING_CENTER_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanAlarmDevice) {
      request = this.setIcon$(ImageType.ALARM_DEVICE_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanSignalElement) {
      request = this.setIcon$(ImageType.SIGNAL_ELEMENT_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanPlasticSign) {
      request = this.setIcon$(ImageType.PLASTIC_SIGN_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanTransmitter) {
      request = this.setIcon$(
        ImageType.TRANSMITTER_ICON,
        (item.getChildren()[1] as Konva.Group).getChildren()[1] as Konva.Image,
        isInGroup,
      );
    } else if (floorplanItem instanceof FloorplanPatientMonitor) {
      request = this.setIcon$(ImageType.PATIENT_MONITOR_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanCentralStation) {
      request = this.setIcon$(ImageType.CENTRAL_STATION_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanTelemetry) {
      request = this.setIcon$(ImageType.TELEMETRY_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanKvm) {
      request = this.setIcon$(ImageType.KVM_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanDisplay) {
      request = this.setIcon$(ImageType.DISPLAY_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanUtility) {
      request = this.setIcon$(ImageType.UTILITY_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    } else if (floorplanItem instanceof FloorplanFlameDetector) {
      request = this.setIcon$(ImageType.FLAME_DETECTOR_ICON, item.getChildren()[1] as Konva.Image, isInGroup);
    }
    if (!request) {
      return throwError(() => new Error("Unknown floorplan item type"));
    }
    return request.pipe(map(() => undefined));
  }

  private getFallbackIconImage(floorplanItem: ProductImageTypes): Konva.Image {
    if (floorplanItem instanceof FloorplanGasWarningCenter) {
      return this.createImage(ImageType.GAS_WARNING_CENTER_ICON);
    } else if (floorplanItem instanceof FloorplanAlarmDevice) {
      return this.createImage(ImageType.ALARM_DEVICE_ICON);
    } else if (floorplanItem instanceof FloorplanSignalElement) {
      return this.createImage(ImageType.SIGNAL_ELEMENT_ICON);
    } else if (floorplanItem instanceof FloorplanPlasticSign) {
      return this.createImage(ImageType.PLASTIC_SIGN_ICON);
    } else if (floorplanItem instanceof FloorplanTransmitter) {
      return this.createImage(ImageType.TRANSMITTER_ICON);
    } else if (floorplanItem instanceof FloorplanPatientMonitor) {
      return this.createImage(ImageType.PATIENT_MONITOR_ICON);
    } else if (floorplanItem instanceof FloorplanCentralStation) {
      return this.createImage(ImageType.CENTRAL_STATION_ICON);
    } else if (floorplanItem instanceof FloorplanTelemetry) {
      return this.createImage(ImageType.TELEMETRY_ICON);
    } else if (floorplanItem instanceof FloorplanKvm) {
      return this.createImage(ImageType.KVM_ICON);
    } else if (floorplanItem instanceof FloorplanDisplay) {
      return this.createImage(ImageType.DISPLAY_ICON);
    } else if (floorplanItem instanceof FloorplanUtility) {
      return this.createImage(ImageType.UTILITY_ICON);
    } else if (floorplanItem instanceof FloorplanFlameDetector) {
      return this.createImage(ImageType.FLAME_DETECTOR_ICON);
    }
    throw new Error("Unknown floorplan item type");
  }

  getScaledSize(width: number, height: number, maxSize: number) {
    const aspectRatio = width / height;

    if (aspectRatio >= 1) {
      return {
        scaledWidth: maxSize,
        scaledHeight: maxSize / aspectRatio,
      };
    } else {
      return {
        scaledWidth: maxSize * aspectRatio,
        scaledHeight: maxSize,
      };
    }
  }

  private getImageUrl$(floorplanItem: ProductImageTypes): Observable<string> {
    if (floorplanItem instanceof FloorplanPatientMonitor) {
      return this.monitoringProductImageService.resolvePatientMonitorImageUrl(floorplanItem.config);
    } else if (floorplanItem instanceof FloorplanCentralStation) {
      return this.monitoringProductImageService.resolveCentralStationImageUrl(floorplanItem.config);
    } else if (floorplanItem instanceof FloorplanTelemetry) {
      return this.monitoringProductImageService.resolveTelemetryImageUrl(floorplanItem.config);
    } else if (floorplanItem instanceof FloorplanUtility) {
      return this.utilityProductService.resolveImageUrl(floorplanItem.config).pipe(map((url) => url ?? ""));
    } else if (floorplanItem instanceof FloorplanDisplay) {
      return of(ProductImage.getCantoSourceUrl(floorplanItem.config.display.option.groupImageCantoID ?? ""));
    } else if (floorplanItem instanceof FloorplanKvm) {
      const transmitterProductId = floorplanItem.config.transmitterProductId;
      const fallbackUrl = floorplanItem.config.transmitterGroupImageCantoId
        ? ProductImage.getCantoSourceUrl(floorplanItem.config.transmitterGroupImageCantoId)
        : "";

      if (!transmitterProductId) {
        return of(fallbackUrl);
      }

      return this.monitoringProductService
        .loadDeviceFromDB(transmitterProductId, MonitoringCategory.KVMS, this.productService.getFilterCountry())
        .pipe(
          map((device) =>
            device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : fallbackUrl,
          ),
          catchError(() => of(fallbackUrl)),
        );
    }

    const productId =
      floorplanItem instanceof FloorplanAlarmDevice
        ? floorplanItem.config.productIds[0]
        : floorplanItem instanceof FloorplanFlameDetector
          ? floorplanItem.config.mainProductId
          : floorplanItem.config.productId;
    return this.productService.getCantoImageUrl(productId).pipe(
      map((cantoUrl) => cantoUrl ?? ""),
      catchError(() => of("")),
    );
  }

  setIcon$(type: ImageType, imageNode: Konva.Image, isInGroup?: boolean): Observable<"success"> {
    const url = ImageFactoryKonva.IMAGE_TYPE_URLS.find((entry) => entry.type === type)?.url;

    if (!url || !imageNode) {
      return throwError(() => new Error("Icon type or image node not found"));
    }
    const color = isInGroup ? IconColor.GROUP_COLOR : IconColor.DEFAULT_COLOR;

    return fetchAndModifySvg(url, color).pipe(
      switchMap(
        (dataUrl) =>
          new Observable<"success">((observer) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";

            img.onload = () => {
              const maxSize = 24;
              const { scaledWidth, scaledHeight } = this.getScaledSize(img.width, img.height, maxSize);

              imageNode.image(img);
              imageNode.width(scaledWidth);
              imageNode.height(scaledHeight);
              imageNode.offsetX((scaledWidth - maxSize) / -2);
              imageNode.offsetY((scaledHeight - maxSize) / -2);

              this.removeBorder(imageNode);

              observer.next("success");
              observer.complete();
            };

            img.onerror = () => observer.error(new Error("Failed to load icon image: " + url));
            img.src = dataUrl;
          }),
      ),
    );
  }

  static create(
    productService: ProductService,
    monitoringProductService: MonitoringProductService,
    monitoringProductImageService: MonitoringProductImageService,
    utilityProductService: UtilityProductService,
  ): Observable<ImageFactoryKonva> {
    return forkJoin(ImageFactoryKonva.IMAGE_TYPE_URLS.map((url) => this.loadImage(url))).pipe(
      map(
        (images) =>
          new ImageFactoryKonva(
            images.reduce(function (map: Map<ImageType, Konva.Image>, obj) {
              map.set(obj.type, obj.image);
              return map;
            }, new Map()),
            productService,
            monitoringProductService,
            monitoringProductImageService,
            utilityProductService,
          ),
      ),
    );
  }

  private static loadImage(imageTypeUrl: ImageTypeUrl): Observable<{ type: ImageType; image: Konva.Image }> {
    return fetchAndModifySvg(imageTypeUrl.url, IconColor.DEFAULT_COLOR).pipe(
      switchMap(
        (dataUrl) =>
          new Observable<{ type: ImageType; image: Konva.Image }>((subscriber) => {
            Konva.Image.fromURL(dataUrl, (image) => {
              subscriber.next({ type: imageTypeUrl.type, image });
              subscriber.complete();
            });
          }),
      ),
    );
  }

  private removeBorder(imageNode: Konva.Image) {
    imageNode.stroke(null);
    imageNode.strokeWidth(0);
  }

  private setBorder(imageNode: Konva.Image, color: IconColor) {
    const img = imageNode.image() as HTMLImageElement;
    if (!img) return;

    //StrokeWidth behaves differently for images with a transparent background. Because of this, all images get filled with a default white background
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    canvas.width = img.width;
    canvas.height = img.height;

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(img, 0, 0);

    imageNode.image(canvas);

    imageNode.stroke(color);
    imageNode.strokeWidth(2);
    imageNode.cornerRadius(1);
  }
}

function fetchAndModifySvg(url: string, fillColor: string): Observable<string> {
  return fromFetch(url).pipe(
    switchMap((response) => response.text()),
    map((svgText) => replaceFill(svgText, fillColor)),
    map((modifiedSvg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(modifiedSvg)}`),
  );
}

function replaceFill(svgText: string, targetColor: string): string {
  return svgText.replace(/fill="white"/gi, `fill="${targetColor}"`).replace(/stroke="white"/gi, `stroke="${targetColor}"`);
}

/**
 * Recolors a Konva icon SVG data URL by replacing the baked-in icon color with
 * toColor. Handles both DEFAULT_COLOR and GROUP_COLOR variants.
 * Returns the original dataUrl unchanged if it is not an SVG data URL.
 */
export function recolorSvgDataUrl(dataUrl: string, toColor: string): string {
  const prefix = "data:image/svg+xml;charset=utf-8,";
  if (!dataUrl.startsWith(prefix)) return dataUrl;
  const svgText = decodeURIComponent(dataUrl.slice(prefix.length));
  const recolored = svgText
    .replace(new RegExp(`fill="${IconColor.DEFAULT_COLOR}"`, "gi"), `fill="${toColor}"`)
    .replace(new RegExp(`stroke="${IconColor.DEFAULT_COLOR}"`, "gi"), `stroke="${toColor}"`)
    .replace(new RegExp(`fill="${IconColor.GROUP_COLOR}"`, "gi"), `fill="${toColor}"`)
    .replace(new RegExp(`stroke="${IconColor.GROUP_COLOR}"`, "gi"), `stroke="${toColor}"`);
  return prefix + encodeURIComponent(recolored);
}
